'use client';

import { BuildingOffice2Icon, EnvelopeIcon } from '@heroicons/react/24/outline';
import dynamic from 'next/dynamic';
import { type FC, useEffect, useRef } from 'react';

const ContactEmail = dynamic(() => import('@/components/obf/ContactEmail.client'), { ssr: false });
const _ContactPhone = dynamic(() => import('@/components/obf/ContactPhone.client'), { ssr: false });

import ContactForm from '@/components/forms/ContactForm';
import Eyebrow from '@/components/ui/Eyebrow';
import type { TWithId } from '@/lib/interfaces/shared';
import Tag from '@/ui/Tag';

const Contact: FC<TWithId> = ({ id }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const CELL_SIZE = 200;
  const NUM_COLS = 10;
  const NUM_ROWS = 5;
  const lastIndexRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);
  const rectsRef = useRef<SVGRectElement[] | null>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    rectsRef.current = Array.from(svg.querySelectorAll('rect')) as SVGRectElement[];
  }, []);

  const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('.no-bg-hover')) {
      // clear highlight quickly
      const rects = rectsRef.current;
      if (rects && lastIndexRef.current !== null && rects[lastIndexRef.current]) {
        rects[lastIndexRef.current].setAttribute('fill', 'transparent');
        rects[lastIndexRef.current].style.transition = 'fill 1500ms ease-out';
      }
      lastIndexRef.current = null;
      return;
    }
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mouseX = e.clientX;
    const mouseY = e.clientY;

    if (mouseX < rect.left || mouseX > rect.right || mouseY < rect.top || mouseY > rect.bottom) {
      const rects = rectsRef.current;
      if (rects && lastIndexRef.current !== null && rects[lastIndexRef.current]) {
        rects[lastIndexRef.current].setAttribute('fill', 'transparent');
        rects[lastIndexRef.current].style.transition = 'fill 1500ms ease-out';
      }
      lastIndexRef.current = null;
      return;
    }

    const centerX = rect.left + rect.width / 2;
    const baseTop = rect.top - 1;
    const deltaX = mouseX - centerX;
    const col = Math.floor(deltaX / CELL_SIZE + NUM_COLS / 2);
    const row = Math.floor((mouseY - baseTop) / CELL_SIZE);

    const nextIndex = col >= 0 && col < NUM_COLS && row >= 0 && row < NUM_ROWS ? row * NUM_COLS + col : null;

    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      const rects = rectsRef.current;
      if (!rects) return;
      const prev = lastIndexRef.current;
      if (prev !== null && rects[prev]) {
        rects[prev].setAttribute('fill', 'transparent');
        rects[prev].style.transition = 'fill 1500ms ease-out';
      }
      if (nextIndex !== null && rects[nextIndex]) {
        rects[nextIndex].setAttribute('fill', 'rgb(10 193 141 / 0.10)');
        rects[nextIndex].style.transition = 'fill 200ms ease-in';
      }
      lastIndexRef.current = nextIndex;
    });
  };

  const handleMouseLeave = () => {
    const rects = rectsRef.current;
    if (!rects) return;
    const prev = lastIndexRef.current;
    if (prev !== null && rects[prev]) {
      rects[prev].setAttribute('fill', 'transparent');
      rects[prev].style.transition = 'fill 1500ms ease-out';
    }
    lastIndexRef.current = null;
  };

  return (
    <Tag
      tag='section'
      id={id}
      className='relative isolate overflow-visible bg-primary py-12 sm:py-16'
      inMotion
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-section-title='Contact'
      aria-labelledby='contact-heading'
    >
      <div className='mx-auto max-w-[90rem] lg:px-8'>
        <div className='grid grid-cols-1 overflow-visible lg:grid-cols-2'>
          <div
            className='relative flex overflow-visible border-b border-primary-foreground/10 px-6 pt-12 pb-10 sm:pt-16 lg:static
              lg:justify-center lg:border-r lg:border-b-0 lg:py-24'
          >
            <svg
              aria-hidden='true'
              className='absolute inset-0 size-full mask-[radial-gradient(100%_100%_at_top_right,white,transparent)] stroke-gray-200 lg:hidden dark:stroke-white/10'
            >
              <defs>
                <pattern
                  id='mobile-contact-pattern'
                  width={200}
                  height={200}
                  x='100%'
                  y={-1}
                  patternUnits='userSpaceOnUse'
                >
                  <path d='M130 200V.5M.5 .5H200' fill='none' />
                </pattern>
              </defs>
              <rect width='100%' height='100%' strokeWidth={0} className='fill-primary' />
              <svg x='100%' y={-1} className='overflow-visible fill-primary' aria-hidden='true'>
                <path d='M-470.5 0h201v201h-201Z' strokeWidth={0} />
              </svg>
              <rect width='100%' height='100%' fill='url(#mobile-contact-pattern)' strokeWidth={0} />
            </svg>
            <svg
              aria-hidden='true'
              className='pointer-events-none absolute -z-10 hidden h-full mask-[radial-gradient(100%_100%_at_top_right,white,transparent)] stroke-gray-200 lg:block dark:stroke-white/10'
              style={{ top: 0, left: 'calc(-50vw + 50%)', width: '50vw' }}
            >
              <defs>
                <pattern x='50%' y={-1} id='contact-pattern' width={200} height={200} patternUnits='userSpaceOnUse'>
                  <path d='M.5 200V.5H200' fill='none' />
                </pattern>
              </defs>
              <rect fill='url(#contact-pattern)' width='100%' height='100%' strokeWidth={0} />
            </svg>
            {/* subtle top fade over background grid (mobile + desktop) – sits behind hover rects */}
            <div className='pointer-events-none absolute inset-x-0 top-0 -z-10 h-16 sm:h-20 bg-gradient-to-b from-primary via-primary/60 to-transparent' />
            <svg
              aria-hidden='true'
              className='absolute -z-5 hidden h-full lg:block'
              style={{
                isolation: 'isolate',
                pointerEvents: 'none',
                top: 0,
                left: 'calc(-50vw + 50%)',
                width: '50vw',
              }}
              ref={svgRef}
            >
              {Array.from({ length: NUM_COLS * NUM_ROWS }, (_, i) => {
                const col = i % NUM_COLS;
                const row = Math.floor(i / NUM_COLS);
                const x = (col - NUM_COLS / 2) * CELL_SIZE;
                const y = -1 + row * CELL_SIZE;

                return (
                  <rect
                    key={i}
                    x={`calc(50% + ${x}px)`}
                    y={y}
                    width={200}
                    height={200}
                    fill='transparent'
                    style={{ transformOrigin: 'center', transform: 'translateZ(0)' }}
                  />
                );
              })}
            </svg>
            <div className='pointer-events-auto relative z-10 mx-auto max-w-xl overflow-hidden lg:mx-0 lg:max-w-lg [&_a]:pointer-events-auto [&_button]:pointer-events-auto'>
              <Eyebrow size='base' aria-hidden='true'>
                Contact
              </Eyebrow>
              <h2
                id='contact-heading'
                className='mt-2 text-4xl font-normal tracking-tight text-pretty text-secondary sm:text-5xl'
              >
                Get in touch
              </h2>
              <p className='mt-6 text-base/7 text-secondary-foreground'>
                UAE-based, serving clients worldwide. Share a few details and we'll schedule a time for your zone.
              </p>
              <dl className='mt-10 grid grid-cols-[auto_1fr] gap-x-4 gap-y-4 text-base/7 text-secondary-foreground'>
                <dt className='flex-none'>
                  <span className='sr-only'>Address</span>
                  <BuildingOffice2Icon aria-hidden='true' className='h-7 w-6 text-secondary-foreground' />
                </dt>
                <dd>
                  44210 IFZA Business Park
                  <br />
                  Dubai
                  <br />
                  United Arab Emirates
                </dd>
                {/* <dt className='flex-none'>
                  <span className='sr-only'>Telephone</span>
                  <PhoneIcon aria-hidden='true' className='h-7 w-6 text-secondary-foreground' />
                </dt>
                <dd>
                  <span className='pointer-events-auto text-secondary-foreground hover:text-secondary no-bg-hover'>
                    <ContactPhone />
                  </span>
                </dd> */}
                <dt className='flex-none'>
                  <span className='sr-only'>Email</span>
                  <EnvelopeIcon aria-hidden='true' className='h-7 w-6 text-secondary-foreground' />
                </dt>
                <dd>
                  <span className='pointer-events-auto text-secondary-foreground hover:text-secondary no-bg-hover'>
                    <ContactEmail />
                  </span>
                </dd>
              </dl>
            </div>
          </div>
          <div className='flex w-full items-center justify-center bg-primary px-6 pt-12 pb-10 lg:px-8 lg:py-24'>
            <ContactForm />
          </div>
        </div>
      </div>
    </Tag>
  );
};

export default Contact;
